import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
	Box,
	Button,
	TextField,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Typography,
	Paper,
	CircularProgress,
} from "@mui/material";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL; // e.g. https://api.example.com/
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

function wsUrlForDevice(deviceId) {
	try {
		const u = new URL(BACKEND_URL);
		u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
		// WS endpoint assumed on same host as backend
		u.pathname = `/ws/ota/${deviceId}/`;
		u.search = "";
		u.hash = "";
		return u.toString();
	} catch {
		const { protocol, host } = window.location;
		const scheme = protocol === "https:" ? "wss" : "ws";
		return `${scheme}://${host}/ws/ota/${deviceId}/`;
	}
}

export default function Commands({ deviceId }) {
	const commands = [
		{ label: "Get Configuration", value: "get_config" },
		{ label: "Set Configuration", value: "set_config" },
		{ label: "Immobilize", value: "immobilize" },
		{ label: "Mobilize", value: "mobilize" },
		{ label: "OTA", value: "ota" },
	];

	const [selected, setSelected] = useState("get_config");
	const [configValue, setConfigValue] = useState("");
	const [urlValue, setUrlValue] = useState("");
	const [response, setResponse] = useState("");
	const [loading, setLoading] = useState(false);
	const [otaActive, setOtaActive] = useState(false);

	const wsRef = useRef(null);

	// Clean up WS on unmount or mode change
	useEffect(() => {
		return () => {
			if (wsRef.current) {
				wsRef.current.close();
				wsRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		// If user switches away from OTA, close any open OTA stream
		if (selected !== "ota" && wsRef.current) {
			wsRef.current.close();
			wsRef.current = null;
			setOtaActive(false);
		}
	}, [selected]);

	const appendLine = (line) =>
		setResponse((prev) => (prev ? `${prev}\n${line}` : line));

	const openOtaWebSocket = () => {
		const url = wsUrlForDevice(deviceId);
		const ws = new WebSocket(url);
		wsRef.current = ws;
		setOtaActive(true);

		ws.onopen = () => appendLine("[WS] Connected for OTA progress");
		ws.onmessage = (e) => {
			try {
				const d = JSON.parse(e.data); // {device_id, stage, percent?, message?}
				const pct = d.percent != null ? ` ${d.percent}%` : "";
				const msg = d.message ? ` ${d.message}` : "";
				appendLine(`[OTA] ${d.stage}${pct}${msg}`);
				if (d.stage === "complete" || d.stage === "error") {
					ws.close();
				}
			} catch {
				appendLine(`[WS] ${e.data}`);
			}
		};
		ws.onclose = () => {
			appendLine("[WS] Closed");
			wsRef.current = null;
			setOtaActive(false);
		};
		ws.onerror = () => appendLine("[WS] Error");
	};

	const handleSend = async () => {
		setLoading(true);
		setResponse("");
		const payload = { action: selected };
		if (selected === "set_config") payload.config = configValue;
		if (selected === "ota") payload.url = urlValue;

		try {
			const res = await fetch(
				`${BACKEND_URL}devices/${deviceId}/command/`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${BACKEND_TOKEN}`,
					},
					body: JSON.stringify(payload),
				}
			);

			const data = await res.json();

			if (selected !== "ota") {
				setResponse(JSON.stringify(data, null, 2));
			} else {
				// OTA: start WS stream and show backend ack inline
				if (!res.ok) {
					setResponse(JSON.stringify(data, null, 2));
				} else {
					appendLine("[API] OTA command accepted");
					// open per-device WS only for OTA
					openOtaWebSocket();
				}
			}
		} catch (err) {
			setResponse(`Error: ${err.message}`);
		} finally {
			setLoading(false);
		}
	};

	return (
		<Paper sx={{ p: 2 }} elevation={2}>
			<FormControl fullWidth sx={{ mb: 2 }}>
				<InputLabel id="command-select-label">Command</InputLabel>
				<Select
					labelId="command-select-label"
					value={selected}
					label="Command"
					onChange={(e) => setSelected(e.target.value)}
				>
					{commands.map((cmd) => (
						<MenuItem key={cmd.value} value={cmd.value}>
							{cmd.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{selected === "set_config" && (
				<TextField
					fullWidth
					multiline
					minRows={3}
					label="Configuration"
					value={configValue}
					onChange={(e) => setConfigValue(e.target.value)}
					sx={{ mb: 2 }}
				/>
			)}
			{selected === "ota" && (
				<TextField
					fullWidth
					label="OTA URL"
					value={urlValue}
					onChange={(e) => setUrlValue(e.target.value)}
					sx={{ mb: 2 }}
				/>
			)}

			<Button
				variant="contained"
				onClick={handleSend}
				sx={{ mb: 2 }}
				disabled={loading || (selected === "ota" && otaActive)}
			>
				{selected === "ota" && otaActive
					? "Streaming…"
					: "Send Command"}
			</Button>

			<Typography variant="subtitle1" gutterBottom>
				Response:
			</Typography>
			<Box
				component="pre"
				sx={{
					backgroundColor: "#f5f5f5",
					p: 2,
					borderRadius: 1,
					overflow: "auto",
					minHeight: 120,
					maxHeight: 260,
					whiteSpace: "pre-wrap",
					wordBreak: "break-word",
					display: "flex",
					alignItems: response ? "flex-start" : "center",
					justifyContent: response ? "flex-start" : "center",
				}}
			>
				{loading && !response ? (
					<CircularProgress size={20} />
				) : (
					response || "—"
				)}
			</Box>
		</Paper>
	);
}

Commands.propTypes = {
	deviceId: PropTypes.string.isRequired,
};
